/**
 * Dashboard Schedule Module
 * Handles countdown timers, leave schedules, and schedule management
 */
export class DashboardSchedule {
  constructor(dashboardCore) {
    this.dashboard = dashboardCore;
    this.countdowns = [];
    this.leaveSchedule = null;
    this.sleepSettings = null;

    // DOM elements for leave schedule modal
    this.leaveModalEl = null;
    this.leaveFormEl = null;
    this.leaveEntryList = null;
    this.addLeaveEntryButton = null;
    this.activeModalEntries = [];

    // Header countdown elements
    this.headerCountdownEl = null;
    this.headerCountdownLabelEl = null;
    this.headerCountdownValueEl = null;
    this.headerCountdownMetaEl = null;
    this.headerCountdownActionEl = null;

    // Leave modal state
    this.leaveModalPreviousFocus = null;
  }

  async initialize() {
    // Initialize header countdown elements
    this.headerCountdownEl = document.getElementById('header-countdown');
    this.headerCountdownLabelEl = document.getElementById('header-countdown-label');
    this.headerCountdownValueEl = document.getElementById('header-countdown-value');
    this.headerCountdownMetaEl = document.getElementById('header-countdown-meta');
    this.headerCountdownActionEl = document.getElementById('header-leave-schedule-button');

    // Initialize leave schedule modal elements
    this.leaveModalEl = document.getElementById('leave-schedule-modal');
    this.leaveFormEl = document.getElementById('leave-schedule-form');
    this.leaveEntryList = document.getElementById('leave-entry-list');
    this.addLeaveEntryButton = document.getElementById('add-leave-entry-button');

    this.setupEventListeners();
    this.loadScheduleData();
    this.updateHeaderCountdown();
  }

  setupEventListeners() {
    // Modal close handlers
    if (this.leaveModalEl) {
      this.leaveModalEl.addEventListener('click', (event) => {
        if (event.target?.dataset?.leaveAction === 'close') {
          event.preventDefault();
          this.closeLeaveScheduleModal();
        }
      });

      this.leaveModalEl.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeLeaveScheduleModal();
        }
      });
    }

    // Form submission
    if (this.leaveFormEl) {
      this.leaveFormEl.addEventListener('submit', (event) => {
        event.preventDefault();
        this.handleLeaveScheduleSubmit();
      });
    }

    // Entry interactions
    if (this.leaveEntryList) {
      this.leaveEntryList.addEventListener('click', (event) => this.handleLeaveEntryClick(event));
    }

    if (this.addLeaveEntryButton) {
      this.addLeaveEntryButton.addEventListener('click', () => this.addLeaveEntryRow());
    }

    // Data subscription for countdown changes
    if (typeof this.dashboard.data.subscribe === 'function') {
      this.dashboard.data.subscribe('countdowns', () => {
        const workCountdown = this.dashboard.getCountdownById(this.dashboard.countdownIds?.work);
        this.leaveSchedule = this.normalizeLeaveSchedule(workCountdown?.schedule);
        this.populateLeaveScheduleForm();
        this.updateHeaderCountdown();
      });
    }
  }

  loadScheduleData() {
    // Load existing schedule data
    const workCountdown = this.dashboard.getCountdownById(this.dashboard.countdownIds?.work);
    const sleepCountdown = this.dashboard.getCountdownById(this.dashboard.countdownIds?.sleep);

    if (workCountdown?.schedule) {
      this.leaveSchedule = this.normalizeLeaveSchedule(workCountdown.schedule);
      this.dashboard.data.set('leaveSchedule', this.leaveSchedule);
    }

    if (sleepCountdown?.settings) {
      this.sleepSettings = this.normalizeSleepSettings(sleepCountdown.settings);
      this.dashboard.data.set('sleepSettings', this.sleepSettings);
    }

    this.populateLeaveScheduleForm();
  }

  // Schedule normalization methods
  normalizeSleepSettings(rawSettings = {}) {
    const base = typeof rawSettings === 'object' && rawSettings !== null ? rawSettings : {};
    const bedtime = /^\d{2}:\d{2}$/.test(String(base.bedtime))
      ? String(base.bedtime)
      : this.dashboard.sleepSettingsDefaults.bedtime;
    let windDownMinutes = Number.parseInt(base.windDownMinutes, 10);
    if (!Number.isFinite(windDownMinutes) || windDownMinutes < 0) {
      windDownMinutes = this.dashboard.sleepSettingsDefaults.windDownMinutes;
    }
    const configured = Boolean(base.configured);
    return {
      bedtime,
      windDownMinutes,
      configured,
    };
  }

  normalizeLeaveSchedule(rawSchedule = {}) {
    const inputEntries = Array.isArray(rawSchedule?.entries)
      ? [...rawSchedule.entries]
      : [];

    if (!inputEntries.length && rawSchedule?.leaveTime) {
      inputEntries.push({
        id: rawSchedule.id,
        label: rawSchedule.label ?? '',
        leaveTime: rawSchedule.leaveTime,
        buffer: rawSchedule.buffer,
        days: Array.isArray(rawSchedule.workDays) ? rawSchedule.workDays : [],
      });
    }

    const normalizedEntries = inputEntries
      .map((entry) => this.normalizeLeaveEntry(entry))
      .filter((entry) => entry.days.length > 0 && /^\d{2}:\d{2}$/.test(entry.leaveTime));

    if (!normalizedEntries.length && Array.isArray(this.dashboard.leaveScheduleDefaults.entries)) {
      normalizedEntries.push(
        ...this.dashboard.leaveScheduleDefaults.entries.map((entry) => this.normalizeLeaveEntry(entry))
      );
    }

    const configured = Boolean(rawSchedule?.configured) && normalizedEntries.length > 0;

    return {
      entries: normalizedEntries,
      configured,
    };
  }

  normalizeLeaveEntry(entry) {
    const base = typeof entry === 'object' && entry !== null ? entry : {};
    const id = base.id || this.dashboard.generateLeaveEntryId();
    const leaveTime = /^\d{2}:\d{2}$/.test(String(base.leaveTime))
      ? String(base.leaveTime)
      : '08:30';
    let buffer = Number.parseInt(base.buffer, 10);
    buffer = Number.isFinite(buffer) ? Math.min(Math.max(buffer, 0), 180) : 15;
    const days = Array.isArray(base.days)
      ? Array.from(new Set(base.days.filter((day) => this.dashboard.leaveDayOrder.includes(day))))
      : [];

    return {
      id,
      label: (base.label ?? '').trim(),
      leaveTime,
      buffer,
      days,
    };
  }

  // Modal management
  populateLeaveScheduleForm() {
    if (!this.leaveFormEl) {
      return;
    }

    this.activeModalEntries =
      this.leaveSchedule?.entries?.map((entry) => this.normalizeLeaveEntry(entry)) ??
      this.dashboard.leaveScheduleDefaults.entries.map((entry) => this.normalizeLeaveEntry(entry));

    this.renderLeaveEntries();
  }

  renderLeaveEntries() {
    if (!this.leaveEntryList) {
      return;
    }

    this.leaveEntryList.innerHTML = '';
    const fragment = document.createDocumentFragment();

    const total = this.activeModalEntries.length;
    this.activeModalEntries.forEach((entry) => {
      fragment.appendChild(this.buildLeaveEntryRow(entry, total));
    });

    this.leaveEntryList.appendChild(fragment);
  }

  buildLeaveEntryRow(entry, totalCount = 1) {
    const row = document.createElement('div');
    row.className = 'leave-entry';
    row.dataset.entryId = entry.id;

    const headerRow = document.createElement('div');
    headerRow.className = 'leave-entry-row';

    // Label field
    const labelField = document.createElement('label');
    labelField.className = 'leave-entry-field leave-entry-label';
    labelField.innerHTML = `<span>Label (optional)</span>`;
    const labelInput = document.createElement('input');
    labelInput.type = 'text';
    labelInput.placeholder = 'Job or shift name';
    labelInput.value = entry.label ?? '';
    labelField.appendChild(labelInput);

    // Time field
    const timeField = document.createElement('label');
    timeField.className = 'leave-entry-field leave-entry-time';
    timeField.innerHTML = `<span>Leave time</span>`;
    const timeInput = document.createElement('input');
    timeInput.type = 'time';
    timeInput.required = true;
    timeInput.value = entry.leaveTime ?? '08:30';
    timeField.appendChild(timeInput);

    // Buffer field
    const bufferField = document.createElement('label');
    bufferField.className = 'leave-entry-field leave-entry-buffer';
    bufferField.innerHTML = `<span>Buffer (min)</span>`;
    const bufferInput = document.createElement('input');
    bufferInput.type = 'number';
    bufferInput.min = '0';
    bufferInput.max = '180';
    bufferInput.step = '5';
    bufferInput.value = Number.isFinite(entry.buffer) ? String(entry.buffer) : '15';
    bufferField.appendChild(bufferInput);

    // Remove button
    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'leave-entry-remove';
    removeButton.dataset.action = 'remove-entry';
    removeButton.textContent = 'Remove';
    if (totalCount <= 1) {
      removeButton.disabled = true;
      removeButton.classList.add('is-disabled');
      removeButton.title = 'At least one schedule is required';
    }

    headerRow.appendChild(labelField);
    headerRow.appendChild(timeField);
    headerRow.appendChild(bufferField);
    headerRow.appendChild(removeButton);

    // Days selection
    const dayGroup = document.createElement('div');
    dayGroup.className = 'leave-entry-days';
    dayGroup.setAttribute('role', 'group');
    dayGroup.setAttribute('aria-label', 'Work days');

    this.dashboard.leaveDayOrder.forEach((dayCode) => {
      const meta = this.dashboard.leaveDayMeta[dayCode];
      const dayButton = document.createElement('button');
      dayButton.type = 'button';
      dayButton.className = 'leave-day-chip';
      dayButton.dataset.day = dayCode;
      dayButton.dataset.action = 'toggle-day';
      dayButton.textContent = meta.short;
      const isSelected = entry.days.includes(dayCode);
      dayButton.classList.toggle('is-selected', isSelected);
      dayButton.setAttribute('aria-pressed', String(isSelected));
      dayButton.title = meta.label;
      dayGroup.appendChild(dayButton);
    });

    row.appendChild(headerRow);
    row.appendChild(dayGroup);

    return row;
  }

  handleLeaveEntryClick(event) {
    const actionTarget = event.target.closest('[data-action]');
    if (!actionTarget) {
      return;
    }

    const entryElement = actionTarget.closest('.leave-entry');
    if (!entryElement) {
      return;
    }

    const entryId = entryElement.dataset.entryId;
    if (!entryId) {
      return;
    }

    switch (actionTarget.dataset.action) {
      case 'toggle-day':
        event.preventDefault();
        this.syncEntryFormState();
        this.toggleEntryDay(entryId, actionTarget.dataset.day, actionTarget);
        break;
      case 'remove-entry':
        event.preventDefault();
        this.syncEntryFormState();
        this.removeLeaveEntry(entryId);
        break;
      default:
        break;
    }
  }

  addLeaveEntryRow() {
    const newEntry = this.dashboard.createLeaveEntry('', '08:30', 15, []);
    this.activeModalEntries.push(newEntry);
    this.renderLeaveEntries();
    this.syncEntryFormState();
  }

  removeLeaveEntry(entryId) {
    this.activeModalEntries = this.activeModalEntries.filter(entry => entry.id !== entryId);
    this.renderLeaveEntries();
    this.syncEntryFormState();
  }

  toggleEntryDay(entryId, dayCode, buttonElement) {
    const entry = this.activeModalEntries.find(e => e.id === entryId);
    if (!entry) {
      return;
    }

    const dayIndex = entry.days.indexOf(dayCode);
    if (dayIndex >= 0) {
      entry.days.splice(dayIndex, 1);
      buttonElement.classList.remove('is-selected');
      buttonElement.setAttribute('aria-pressed', 'false');
    } else {
      entry.days.push(dayCode);
      buttonElement.classList.add('is-selected');
      buttonElement.setAttribute('aria-pressed', 'true');
    }
  }

  syncEntryFormState() {
    // Sync form state with active modal entries
    if (!this.leaveEntryList) {
      return;
    }

    const entries = Array.from(this.leaveEntryList.querySelectorAll('.leave-entry'));
    this.activeModalEntries = entries.map(entryElement => {
      const entryId = entryElement.dataset.entryId;
      const existingEntry = this.activeModalEntries.find(e => e.id === entryId);

      if (existingEntry) {
        // Update existing entry
        const labelInput = entryElement.querySelector('.leave-entry-label input');
        const timeInput = entryElement.querySelector('.leave-entry-time input');
        const bufferInput = entryElement.querySelector('.leave-entry-buffer input');

        existingEntry.label = (labelInput?.value ?? '').trim();
        existingEntry.leaveTime = timeInput?.value ?? '08:30';
        existingEntry.buffer = Number.isFinite(Number(bufferInput?.value))
          ? Number(bufferInput.value)
          : 15;

        // Update days
        const dayButtons = entryElement.querySelectorAll('.leave-day-chip.is-selected');
        existingEntry.days = Array.from(dayButtons).map(button => button.dataset.day);
      }

      return existingEntry;
    });
  }

  handleLeaveScheduleSubmit() {
    this.syncEntryFormState();

    // Save to dashboard data
    const schedule = {
      entries: this.activeModalEntries,
      configured: true,
    };

    this.dashboard.data.set('leaveSchedule', schedule);

    // Update work countdown if it exists
    const workCountdown = this.dashboard.getCountdownById(this.dashboard.countdownIds?.work);
    if (workCountdown) {
      workCountdown.schedule = schedule;
      this.dashboard.updateCountdown(this.dashboard.countdownIds.work, { schedule });
    }

    this.closeLeaveScheduleModal();
    this.dashboard.showNotification('✨ Leave schedule updated!');
  }

  closeLeaveScheduleModal() {
    if (this.leaveModalEl) {
      this.leaveModalEl.setAttribute('hidden', '');
    }
  }

  // Header countdown functionality
  updateHeaderCountdown(now = new Date()) {
    if (!this.headerCountdownEl ||
        !this.headerCountdownLabelEl ||
        !this.headerCountdownValueEl) {
      return;
    }

    const statusClasses = [
      'header-countdown--warning',
      'header-countdown--critical',
      'header-countdown--empty',
    ];

    this.headerCountdownEl.classList.remove(...statusClasses);

    if (!this.leaveSchedule?.entries?.length) {
      this.headerCountdownEl.classList.add('header-countdown--empty');
      this.headerCountdownLabelEl.textContent = 'Leave for work';
      this.headerCountdownValueEl.textContent = '--';
      if (this.headerCountdownMetaEl) {
        this.headerCountdownMetaEl.textContent = '';
        this.headerCountdownMetaEl.style.display = 'none';
      }
      if (this.headerCountdownActionEl) {
        this.headerCountdownActionEl.textContent = 'Set schedule';
        this.headerCountdownActionEl.style.display = '';
        this.headerCountdownActionEl.disabled = false;
      }
      return;
    }

    // Find next leave time
    const nextLeave = this.getNextLeaveTime(now);
    if (!nextLeave) {
      this.headerCountdownEl.classList.add('header-countdown--empty');
      this.headerCountdownLabelEl.textContent = 'Leave for work';
      this.headerCountdownValueEl.textContent = 'No upcoming';
      if (this.headerCountdownMetaEl) {
        this.headerCountdownMetaEl.textContent = 'Check your schedule';
        this.headerCountdownMetaEl.style.display = '';
      }
      return;
    }

    const { entry, leaveTime } = nextLeave;
    const timeUntilLeave = leaveTime.getTime() - now.getTime();
    const hoursUntilLeave = timeUntilLeave / (1000 * 60 * 60);

    // Update label and value
    this.headerCountdownLabelEl.textContent = entry.label || 'Leave for work';
    this.headerCountdownValueEl.textContent = entry.primaryText || '--';

    if (this.headerCountdownMetaEl) {
      this.headerCountdownMetaEl.textContent = entry.secondaryText;
      this.headerCountdownMetaEl.style.display = '';
    }

    // Update action button
    if (this.headerCountdownActionEl) {
      const canWrite = this.dashboard.oauthClient?.hasScopes?.(this.dashboard.GoogleCalendarService.WRITE_SCOPES) ?? false;
      const actionLabel = canWrite ? 'Schedule in Calendar' : 'Adjust';
      this.headerCountdownActionEl.textContent = actionLabel;
      this.headerCountdownActionEl.style.display = '';
      this.headerCountdownActionEl.disabled = false;
      this.headerCountdownActionEl.onclick = canWrite
        ? () => this.dashboard.scheduleLeaveInCalendar(entry)
        : null;
    }

    // Apply status styling
    if (hoursUntilLeave <= 1) {
      this.headerCountdownEl.classList.add('header-countdown--critical');
    } else if (hoursUntilLeave <= 2) {
      this.headerCountdownEl.classList.add('header-countdown--warning');
    }
  }

  getNextLeaveTime(from = new Date()) {
    const today = from.getDay();
    const currentTime = from.getHours() * 60 + from.getMinutes();

    for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
      const checkDay = (today + dayOffset) % 7;
      const dayCode = this.dashboard.leaveDayOrder[checkDay];

      for (const entry of this.leaveSchedule.entries) {
        if (!entry.days.includes(dayCode)) {
          continue;
        }

        const [hours, minutes] = entry.leaveTime.split(':').map(Number);
        const entryTime = hours * 60 + minutes;

        let checkDate = new Date(from);
        checkDate.setDate(from.getDate() + dayOffset);
        checkDate.setHours(hours, minutes, 0, 0);

        if (dayOffset === 0 && entryTime <= currentTime) {
          // This time has passed today, check next occurrence
          continue;
        }

        const timeUntilLeave = checkDate.getTime() - from.getTime();
        const hoursUntilLeave = timeUntilLeave / (1000 * 60 * 60);

        if (hoursUntilLeave > 24) {
          // More than 24 hours away, format as day and time
          const dayName = this.dashboard.leaveDayMeta[dayCode]?.short || dayCode;
          return {
            entry,
            leaveTime: checkDate,
            primaryText: `${dayName} ${entry.leaveTime}`,
            secondaryText: `${Math.ceil(hoursUntilLeave / 24)} days away`,
          };
        } else if (hoursUntilLeave >= 1) {
          // Less than 24 hours, show hours and minutes
          const fullHours = Math.floor(hoursUntilLeave);
          const minutes = Math.floor((hoursUntilLeave - fullHours) * 60);
          return {
            entry,
            leaveTime: checkDate,
            primaryText: `${fullHours}h ${minutes}m`,
            secondaryText: `Leave at ${entry.leaveTime}`,
          };
        } else {
          // Less than 1 hour, show minutes only
          const minutesUntil = Math.ceil(hoursUntilLeave * 60);
          return {
            entry,
            leaveTime: checkDate,
            primaryText: `${minutesUntil}m`,
            secondaryText: `Leave at ${entry.leaveTime}`,
          };
        }
      }
    }

    return null;
  }
}
